import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Listen,
  Method,
  Event,
  EventEmitter,
} from '@stencil/core';
import Swiper, { Pagination, Navigation, Autoplay, EffectFade } from 'swiper';
import { versionComponent } from '../../global/inject-dependencies';
import { getRandomNumber } from '../../global/utility/utils';

@Component({
  tag: 'ui-slider',
  styleUrl: 'ui-slider.scss',
  shadow: false,
})
export class UiSlider {
  @Element() el: HTMLElement;

  @Prop() slidesPerPage: 1 | 2 | 3 | 4 = 1;

  @Prop() moveMode: 'by-slide' | 'by-page' = 'by-slide';

  // passed in case we have special behaviour for width > 1024px
  @Prop() slidesPerPageAbove1024?: 1 | 2 | 3 | 4 = this.slidesPerPage;

  // this option is still in beta phase for some apps, please use it carefully
  @Prop() breakpointsBase: 'container' | 'window' = 'window';

  @Prop() autoSlideInterval = 0;

  @Prop() uniqueId: string = getRandomNumber();

  @Prop({ reflect: true }) conversion?: 'full-width';

  @Prop() autoHeight = true;

  @Prop() autoInit = true;

  @Prop() animation: 'enter-from-right' | 'crossfade' = 'enter-from-right';

  @Prop() navigationArrowsOnMobile = false;

  @Prop() navigationArrowType: 'below-slider' | 'on-slider' = 'below-slider';

  @Prop() paginationType: 'default' | 'fraction' = 'default';

  /**
   * Max base width of controls that determines when "fraction" pagination style is enforced
   * Set a base width in px to be summed with "bullet" widths (20px per bullet * slide count)
   */
  @Prop() fractionPaginationThreshold = 156;

  @Prop() noLoop = false;

  /**
   * Shows a small part of the next slide on mobile slider
   */
  @Prop() teaseNextSlideOnMobile = false;

  /**
   * Event that is triggered every time slide changes. Emits real index of slide
   * Expected value for first page : activeSliderPageIndex = 0, second : activeSliderPageIndex =2...
   * there can be more slides per page, see @Prop() slidesPerPage
   */
  @Event({ eventName: 'uiSliderSlideChange' })
  slideChangeEvent: EventEmitter;

  @Event({ eventName: 'uiSliderReady' })
  sliderReadyEvent: EventEmitter<{ id: string }>;

  @Listen('componentResized')
  onComponentResized() {
    if (this.autoInit) {
      this.update();
    }
  }

  @Listen('videoPlay', { target: 'window' })
  onVideoPlay(e: CustomEvent) {
    if (e.detail.isInSlider && this.swiper && this.autoSlideInterval > 0) {
      this.swiper.autoplay.stop();
    }
  }

  @Listen('videoPause', { target: 'window' })
  onVideoPause(e: CustomEvent) {
    if (e.detail.isInSlider && this.swiper && this.autoSlideInterval > 0) {
      this.swiper.autoplay.start();
    }
  }

  private wrapper?: HTMLDivElement;

  private swiper: any;

  private options: any;

  private resizeObserver: ResizeObserver;

  getUnClonedChildrenCount(): number {
    return (
      this.wrapper.children.length -
      [].slice
        .call(this.wrapper.children)
        .filter((child) => child.classList.contains('swiper-slide-duplicate'))
        .length
    );
  }

  toggleNavigatinVisibility(visibleSlides) {
    const hideCount =
      this.slidesPerPage > this.getUnClonedChildrenCount()
        ? this.slidesPerPage
        : this.getUnClonedChildrenCount();
    if (hideCount === visibleSlides || this.getUnClonedChildrenCount() === 1) {
      this.el.querySelector('.ui-slider__controls').classList.add('hidden');
    } else {
      this.el.querySelector('.ui-slider__controls').classList.remove('hidden');
    }
  }

  get paginationTypeOption(): string {
    const typeBasedOnLength =
      this.wrapper.children.length > 8 ? 'fraction' : 'bullets';
    return this.paginationType === 'fraction'
      ? this.paginationType
      : typeBasedOnLength;
  }

  componentDidLoad() {
    this.options = {
      autoplay: {
        delay: this.autoSlideInterval,
        disableOnInteraction: false,
      },
      effect: this.animation === 'crossfade' ? 'fade' : 'slide',
      fadeEffect: {
        crossFade: this.animation === 'crossfade',
      },
      /* It is only possible to disable loop if
      there realy is just one slide, because we
      cannot enable/disable loop in breakpoints */
      loop: this.noLoop ? false : !(this.getUnClonedChildrenCount() === 1),
      speed: 500,
      pagination: {
        el: this.el.querySelector(`.swiper-pagination-${this.uniqueId}`),
        clickable: true,
        type: this.paginationTypeOption,
      },
      resizeObserver: false,
      navigation: {
        nextEl: this.el.querySelector(`.swiper-button-next-${this.uniqueId}`),
        prevEl: this.el.querySelector(`.swiper-button-prev-${this.uniqueId}`),
      },
      autoHeight: this.autoHeight,
      slidesPerView: this.teaseNextSlideOnMobile ? 1.5 : 1,
      preventClicks: true,
      preventClicksPropagation: false,
      noSwipingSelector: 'anchor, ui-link', // disable swiping when clicking on links within slides
      centeredSlides: this.teaseNextSlideOnMobile,
      slidesPerGroup: 1,
      spaceBetween: 20,
      breakpointsBase: this.breakpointsBase,
      breakpoints: {
        600: {
          spaceBetween: 25,
          slidesPerView: this.getSlideCountForBpSmall(),
          slidesPerGroup:
            this.moveMode === 'by-page' ? this.getSlideCountForBpSmall() : 1,
          centeredSlides: false,
        },
        768: {
          spaceBetween: 30,
          slidesPerView: this.getSlideCountForBpMedium(),
          slidesPerGroup:
            this.moveMode === 'by-page' ? this.getSlideCountForBpMedium() : 1,
          centeredSlides: false,
        },
        992: {
          spaceBetween: 35,
          slidesPerView: this.slidesPerPage,
          slidesPerGroup: this.moveMode === 'by-page' ? this.slidesPerPage : 1,
          centeredSlides: false,
        },
        1024: {
          spaceBetween: 35,
          slidesPerView: this.slidesPerPageAbove1024,
          slidesPerGroup: this.moveMode === 'by-page' ? this.slidesPerPage : 1,
          centeredSlides: false,
        },
      },
      watchOverflow: true,
      /**
       * By setting "watchSlidesProgress" to true, classes are set to visible slides,
       * which can be used to querySelect them afterwards
       * Needed for this.getVideoIdsOfHiddenSlides()
       */
      watchSlidesProgress: true,
      on: {
        breakpoint: (evt) => {
          switch (evt.currentBreakpoint) {
            case '1024':
              this.toggleNavigatinVisibility(this.slidesPerPageAbove1024);
              break;
            case '992':
              this.toggleNavigatinVisibility(this.slidesPerPage);
              break;
            case '768':
              this.toggleNavigatinVisibility(this.getSlideCountForBpMedium());
              break;
            case '600':
              this.toggleNavigatinVisibility(this.getSlideCountForBpSmall());
              break;
            default:
              this.toggleNavigatinVisibility(1);
          }
        },
        slideChange: (slide) => {
          this.slideChangeEvent.emit({
            activeSliderPageIndex: slide.realIndex,
            /**
             * We're emitting the hidden exlements so other elements can check,
             * if they are currently in view or not.
             * This is currently used to pause videos, if they aren't visible in sliders anymore.
             * */
            videoIdsOfHiddenSlides: this.getVideoIdsOfHiddenSlides(),
          });
        },
      },
    };
    if (this.autoInit) {
      this.initSwiper();
    }

    this.observeResizingOfSliderChildren();
    this.sliderReadyEvent.emit({ id: this.uniqueId });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  private observeResizingOfSliderChildren() {
    this.resizeObserver = new ResizeObserver(() => {
      this.update();
    });

    // TODO: Could be improved by observing if new children are added + observing them, too
    Array.from(this.wrapper.children).forEach((element) => {
      this.resizeObserver.observe(element);
    });
  }

  private getVideoIdsOfHiddenSlides = () => {
    /**
     * The following is a Helper to make the return statement DRY and more self-documenting.
     * One could say, that it would be better to return "this" in the functions instead of "Helper".
     * But: The types of the helper only work, if we return the "Helper" itself.
     * As we don't get any clashing, this is totally fine.
     */
    const Helper = {
      elements: null as NodeListOf<Element>,
      ids: null as string[],
      getVideoElementsInSlides(el: HTMLUnknownElement) {
        Helper.elements = el.querySelectorAll(
          '.swiper-slide [video-src], .swiper-slide [video-id]'
        );
        return Helper;
      },
      getVisibleVideoElementsInSlides(el: HTMLUnknownElement) {
        Helper.elements = el.querySelectorAll(
          '.swiper-slide.swiper-slide-visible [video-src], .swiper-slide.swiper-slide-visible [video-id]'
        );
        return Helper;
      },
      getVideoIdsFromElements() {
        Helper.ids = Array.from(Helper.elements).map(
          (el: any) =>
            (el as HTMLUiTextImageElement).videoSrc ||
            (el as HTMLUiVideoElement).videoId
        );
        return Helper;
      },
      removeDuplicates() {
        Helper.ids = Array.from(new Set(Helper.ids));
        return Helper;
      },
    };

    return Helper.getVideoElementsInSlides(this.wrapper)
      .getVideoIdsFromElements()
      .removeDuplicates()
      .ids.filter(
        (el) =>
          !Helper.getVisibleVideoElementsInSlides(this.wrapper)
            .getVideoIdsFromElements()
            .ids.includes(el)
      );
  };

  getSlideCountForBpSmall() {
    switch (this.slidesPerPage) {
      case 4:
        return 2;
      case 3:
        return 2;
      case 2:
        return 2;
      default:
        return 1;
    }
  }

  getSlideCountForBpMedium() {
    switch (this.slidesPerPage) {
      case 4:
        return 3;
      case 3:
        return 3;
      case 2:
        return 2;
      default:
        return 1;
    }
  }

  private adjustSwiperPagination(): void {
    if (this.swiper) {
      const threshold =
        this.fractionPaginationThreshold + this.getUnClonedChildrenCount() * 20;
      const showFraction: boolean =
        this.el.clientWidth < threshold &&
        this.options.pagination.type === 'bullets';
      const showBullets: boolean =
        this.el.clientWidth > threshold &&
        this.paginationType === 'default' &&
        this.getUnClonedChildrenCount() < 9 &&
        this.options.pagination.type === 'fraction';

      if (showFraction) {
        this.options.pagination.type = 'fraction';
        this.swiper.destroy();
        this.initSwiper();
      } else if (showBullets) {
        this.options.pagination.type = 'bullets';
        this.swiper.destroy();
        this.initSwiper();
      }
    }
  }

  @Method()
  async onAfterResize() {
    this.update();
  }

  @Method()
  async jumpToSlide(index: number) {
    const slidesPerPage = this.slidesPerPage === 1 ? 0 : this.slidesPerPage - 1;
    const slideNo = index + slidesPerPage;
    if (this.noLoop) {
      this.swiper.slideTo(slideNo, 1000, false);
    } else {
      this.swiper.slideToLoop(slideNo, 1000, false);
    }
    if (this.autoSlideInterval > 0) {
      this.swiper.autoplay.stop();

      this.swiper.on('slideChange', function onSlideChange() {
        this.autoplay.start();
      });
    }
  }

  update() {
    this.adjustSwiperPagination();
    this.swiper?.update();
  }

  initSwiper() {
    // eslint-disable-next-line no-restricted-syntax
    for (const slide of [].slice.call(this.wrapper.children) as HTMLElement[]) {
      slide.classList.add('swiper-slide');
    }

    Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

    this.swiper = new Swiper(
      this.el.querySelector(`.swiper-${this.uniqueId}`) as HTMLElement,
      {
        ...this.options,
      }
    );

    if (this.autoSlideInterval === 0 && this.swiper.autoplay.running) {
      this.swiper.autoplay.stop();
    } else {
      this.swiper.autoplay.start();
    }
  }

  render() {
    return (
      <Host
        class={
          this.conversion
            ? `ui-slider ui-slider--${this.conversion}`
            : 'ui-slider'
        }
      >
        <div class={`swiper swiper-${this.uniqueId}`}>
          <div
            class="swiper-wrapper"
            ref={(el) => {
              this.wrapper = el as HTMLDivElement;
            }}
          >
            <slot />
          </div>
        </div>
        <div
          class={{
            'ui-slider__controls': true,
            'ui-slider__controls--onSlider':
              this.navigationArrowType === 'on-slider',
          }}
        >
          <div
            class={`
            swiper-button-prev swiper-button-prev-${this.uniqueId}
            ${this.navigationArrowsOnMobile ? 'active' : ''}`}
          >
            <ui-icon icon="chevron-left" type="system"></ui-icon>
          </div>
          <div
            class={`swiper-pagination swiper-pagination-${this.uniqueId}`}
          ></div>
          <div
            class={`
            swiper-button-next swiper-button-next-${this.uniqueId}
            ${this.navigationArrowsOnMobile ? 'active' : ''}`}
          >
            <ui-icon icon="chevron-right" type="system"></ui-icon>
          </div>
        </div>
      </Host>
    );
  }
}

versionComponent(UiSlider);
